import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { unique } from "helpers/validate_yup";
import "react-phone-number-input/style.css";
import { NumericFormat } from "react-number-format";
import { postContentType, putContentType } from "store/content-type/thunk";

export interface Props {
  id?: string;
  item?: any;
  isCopy?: boolean;
  triggerRefresh?: () => void;
  isShowDetail?: boolean;
  setLinkImage?: any;
  setIsOpen?: any;
  setItemToEdit?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

Yup.addMethod(Yup.array, "unique", unique);

const ContentTypeForm = ({
  id = "",
  item = "",
  triggerRefresh,
  isShowDetail = false,
  setIsOpen,
  setItemToEdit,
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFirstProgress, setIsFirstProgress] = useState<boolean>(true);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);

      const data = {...values};
      const response: any = id
        ? await putContentType(id, data)
        : await postContentType(data);

      if (response?.data || response?.code == 200) {
        setIsLoading((_prev) => false);
        toast(
          `${
            id
              ? t("The content type has been updated.")
              : t("The content type has been created.")
          }`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(`${t("This field is required")}`),
    description: Yup.string().required(`${t("This field is required")}`),
    ordering: Yup.string().required(`${t("This field is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      ordering: 0
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const onCloseClick = () => {
    setIsOpen(false);
    setItemToEdit(false);
  };

  useEffect(() => {
    if(item) {
      formik.setValues(item);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  useEffect(() => {
    document.title = `${t("Create Content Type")} - ${t(
      "Content Type"
    )} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      setIsFirstProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(formik.errors).length]);

  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="xl">
        <ModalHeader toggle={isLoading ? () => {} : onCloseClick}>
          {id ? t("Update Content Type") : t("Create Content Type")}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={
                !isFirstProgress || !!id
                  ? 100 - (100 / 3) * Number(Object.keys(formik.errors).length)
                  : 0
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <>
              <form onSubmit={formik.handleSubmit}>
                <Row
                  className="modal-custom-scroll"
                  style={{ overflowY: "auto", maxHeight: "68vh" }}
                >
                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="name" className="form-label">
                        {t("Name")} <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        className="form-control"
                        placeholder={t("Name")}
                        value={formik?.values?.name}
                        onChange={(event: any) =>
                          formik.setFieldValue("name", event?.target?.value)
                        }
                      />
                      {formik.touched.name && formik.errors.name ? (
                        <div className="text-danger mt-2">
                          {formik.errors.name}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="name" className="form-label">
                        {t("Order")} <span className="text-danger">*</span>
                      </Label>
                      <NumericFormat
                        className="form-control" 
                        value={formik?.values?.ordering} 
                        placeholder={`${t("Order")}`}
                        allowLeadingZeros 
                        allowNegative={false}
                        thousandSeparator="," 
                        isAllowed={(values) => {
                          const { formattedValue, floatValue } = values;
                          return formattedValue === "" || (floatValue || 0) <= 100;
                        }}
                        onValueChange={(values: any) => {
                          const {floatValue} = values;
                          formik.setFieldValue("ordering", floatValue)
                        }}
                      />
                      {formik.touched.ordering && formik.errors.ordering ? (
                        <div className="text-danger mt-2">
                          {formik.errors.ordering}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                  
                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="description" className="form-label">
                        {t("Description")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="description"
                        name="description"
                        type="textarea"
                        rows="5"
                        maxLength={1000}
                        className="form-control"
                        placeholder={t("Description")}
                        value={formik?.values?.description}
                        onChange={(event: any) =>
                          formik.setFieldValue(
                            "description",
                            event?.target?.value
                          )
                        }
                      />
                      {formik.touched.description &&
                      formik.errors.description ? (
                        <div className="text-danger mt-2">
                          {formik.errors.description}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}></Col>
                </Row>

                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end mt-3">
                    {!isShowDetail && (
                      <button type="submit" className="btn btn-primary fs-14">
                        {isLoading ? (
                          <Spinner size="sm" className="me-2"></Spinner>
                        ) : !!id ? (
                          <i className="ri-login-circle-line align-bottom me-2 "></i>
                        ) : (
                          <i className="ri-add-fill align-bottom me-2"></i>
                        )}
                        {id
                          ? t("Update Content Type")
                          : t("Create Content Type")}
                      </button>
                    )}
                  </div>
                </Col>
              </form>
            </>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ContentTypeForm;
